import { useFragment } from "@/api/generated";
import { CustomerFragmentFragmentDoc } from "@/api/generated/graphql";
import { customerQuery } from "@/api/userApi";
import useAuthUser from "@/composables/useAuthUser";
import { featureEnabled, onlyFeatureEnabled, onlyFeaturesEnabled } from "@/config";
import { MoneyFeatures } from "@/config/constants";
import { routeNames } from "@/router/constants";
import type { HeaderBaseLink, HeaderRootLink } from "@/types/headerLinks";
import { useIntl } from "@moroku/money-webapp-components";
import { useQuery } from "@vue/apollo-composable";
import cleanDeep from "clean-deep";
import { computed, readonly } from "vue";
import { useRouter } from "vue-router";
import { PaymentType } from "@/api/paymentApi.ts";
import { useCards } from "@/composables/useCards.ts";
import { formatCardLabel } from "@/utils/cards";

export default function useHeaderLinks() {
  const { t } = useIntl();

  const { authUser, isBroker } = useAuthUser();
  const { cards } = useCards();
  const isUserLoggedIn = computed(() => !!authUser.value);
  const { result: customerResult } = useQuery(customerQuery, {}, { fetchPolicy: "cache-only" });
  const customer = computed(() => useFragment(CustomerFragmentFragmentDoc, customerResult.value?.customer) ?? null);

  const router = useRouter();
  const isSignUpOrSignInView = computed(
    () =>
      router.currentRoute.value.name === routeNames.SIGN_UP_VIEW ||
      router.currentRoute.value.name === routeNames.SIGN_IN_VIEW ||
      router.currentRoute.value.name === routeNames.ONBOARDING_SPLASH,
  );

  const cardLinks = () => {
    const cardLinks: Record<string, HeaderBaseLink> = {};
    cards.value.forEach(card => {
      cardLinks[card.id] = {
        name: formatCardLabel(card),
        routeName: routeNames.CARD_DETAILS,
        params: { cardId: card.id },
      };
    });
    cardLinks.addCard = {
      name: "Apply for a card",
      routeName: routeNames.CARDS_NEW,
    };
    return cardLinks;
  };

  const headerLinksForUser = (options: { isUserLoggedIn: boolean; customerFirstName?: string }): Record<string, HeaderRootLink> => {
    if (!options.isUserLoggedIn) {
      return {
        signUp: {
          name: t("navigation_signIn"),
          routeName: routeNames.SIGN_IN_VIEW,
        },
      };
    }

    return {
      ...(featureEnabled(MoneyFeatures.InternetBanking) && {
        overview: {
          name: t("home_title"),
          routeName: routeNames.FINANCIAL_OVERVIEW,
        },
        cards: {
          name: t("navigation_cards"),
          links: cardLinks(),
        },
        settings: {
          name: t("navigation_banking_settings"),
          routeName: routeNames.INTERNET_BANKING_SETTINGS,
        },
      }),

      ...(featureEnabled(MoneyFeatures.Lending) &&
        !isBroker.value && {
          loanApplication: {
            name: t("navigation_loanDropDown"),
            links: {
              application: {
                name: t("navigation_loanApplication"),
                routeName: routeNames.LOAN_APPLICATION,
              },
              applications: {
                name: t("navigation_loanApplications"),
                routeName: routeNames.LOAN_APPLICATIONS,
              },
            },
          },
        }),

      ...(featureEnabled(MoneyFeatures.Lending) &&
        isBroker.value && {
          loanApplicationBroker: {
            name: t("navigation_broker_loanDropDown"),
            links: {
              application: {
                name: t("navigation_broker_loanApplication"),
                routeName: routeNames.BROKER_LOAN_APPLICATION,
              },
              applications: {
                name: t("navigation_broker_loanApplications"),
                routeName: routeNames.BROKER_LOAN_APPLICATIONS,
              },
            },
          },
        }),
    };
  };

  const headerOptions = computed(() => ({ isUserLoggedIn: isUserLoggedIn.value, customerFirstName: customer.value?.firstName }));

  // Onboarding has no profile or messages to reach, so the name is a label rather than a menu.
  const isOnboardingOnly = computed(() => onlyFeaturesEnabled(MoneyFeatures.Onboarding, MoneyFeatures.BusinessOnboarding));

  const customerFullName = computed(() => [customer.value?.firstName, customer.value?.lastName].filter(Boolean).join(" ") || undefined);

  const profileLinks = computed(() => {
    if (isOnboardingOnly.value) {
      return {
        profile: {
          name: customerFullName.value ?? t("navigation_profile"),
          growBefore: true,
        },
      };
    }

    return {
      profile: {
        name: customer.value?.firstName ?? t("navigation_profile"),
        links: {
          profile: {
            name: t("navigation_profile"),
            routeName: routeNames.USER_PROFILE,
          },
          ...(featureEnabled(MoneyFeatures.InternetBanking) && {
            messages: {
              name: t("navigation_messages"),
              routeName: routeNames.MESSAGES,
            },
          }),
        },
        growBefore: true,
      },
    };
  });

  const headerLinks = computed(() => {
    if (isSignUpOrSignInView.value || onlyFeatureEnabled(MoneyFeatures.Onboarding) || onlyFeatureEnabled(MoneyFeatures.Lending)) {
      return {};
    }

    return headerLinksForUser(headerOptions.value);
  });

  const mobileHeaderLinks = (): Record<string, HeaderBaseLink> => {
    const baseLinks = headerLinks.value;

    const paymentLinks = featureEnabled(MoneyFeatures.InternetBanking)
      ? {
          transfers: {
            name: t("navigation_transfer"),
            routeName: routeNames.TRANSFER_VIEW,
          },
          bankTransfer: {
            name: t("navigation_bankTransfer"),
            routeName: routeNames.PAYMENT_VIEW,
            query: PaymentType.BankTransfer,
          },
          bpay: {
            name: t("navigation_bpay"),
            routeName: routeNames.PAYMENT_VIEW,
            query: PaymentType.BPAY,
          },
          payId: {
            name: t("navigation_payId"),
            routeName: routeNames.PAYMENT_VIEW,
            query: PaymentType.PayID,
          },
        }
      : <Record<string, HeaderBaseLink>>{};

    return cleanDeep({
      overview: baseLinks.overview,
      ...paymentLinks,
      cards: baseLinks.cards,
      profile: profileLinks.value.profile.links?.profile,
      messages: profileLinks.value.profile.links?.messages,
      logOut: baseLinks.profile?.links?.logOut,
      signUp: baseLinks.signUp,
    });
  };

  const mobileLinks = computed(() => mobileHeaderLinks());

  const topRowLinks = computed<Record<string, HeaderRootLink>>(() => {
    if (headerOptions.value.isUserLoggedIn) {
      // Internet banking uses BankingContextSelector for profile + context switching.
      if (featureEnabled(MoneyFeatures.InternetBanking)) {
        return {} as Record<string, HeaderRootLink>;
      }
      return profileLinks.value;
    }

    if (isSignUpOrSignInView.value) {
      return {} as Record<string, HeaderRootLink>;
    }

    return {
      signUp: {
        name: t("navigation_signIn"),
        routeName: routeNames.SIGN_IN_VIEW,
        growBefore: true,
      },
    };
  });

  const paymentLinks = [
    {
      label: t("navigation_transferOption"),
      command: async () => {
        await router.push({ name: routeNames.TRANSFER_VIEW });
      },
    },
    {
      label: t("navigation_bankTransferOption"),
      command: async () => {
        await router.push({ name: routeNames.PAYMENT_VIEW, query: { type: PaymentType.BankTransfer } });
      },
    },
    {
      label: t("navigation_payIdOption"),
      command: async () => {
        await router.push({ name: routeNames.PAYMENT_VIEW, query: { type: PaymentType.PayID } });
      },
    },
    {
      label: t("navigation_bpayOption"),
      command: async () => {
        await router.push({ name: routeNames.PAYMENT_VIEW, query: { type: PaymentType.BPAY } });
      },
    },
  ];

  return {
    headerLinks: readonly(headerLinks),
    mobileLinks: readonly(mobileLinks),
    topRowLinks: readonly(topRowLinks),
    paymentLinks,
  };
}
